import {
  createContext,
  CSSProperties,
  ReactNode,
  RefCallback,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DEFAULT_STATUS_TABS_HEIGHT = 54;

/**
 * sticky controls 영역의 "펼침(full) → 축소(compact)" 높이 메트릭.
 * 모든 값은 px 단위이며, --order-compact (0~1) 변수로 선형 보간된다.
 * compact 상태에서도 market/controller 행은 최소 높이를 유지해 사라지지 않는다.
 */
const CONTROLS_PADDING_TOP = { full: 20, compact: 10 };
const CONTROLS_GAP = { full: 16, compact: 8 };
// controller 가 테이블 카드의 상단부(border 포함)라서 sticky 섹션 하단 여백은 항상 0.
// 0 이 아니면 카드 상단(controller)과 카드 본문(table) 사이에 흰 띠가 생겨 border 가 끊긴다.
const CONTROLS_PADDING_BOTTOM = { full: 0, compact: 0 };
// Figma(1520-11011) controls row: 상하 패딩 20 + 콘텐츠(검색 인풋) 44 = 84.
// compact 는 콘텐츠 44 가 들어가는 최소 높이(+상하 4px)로 줄인다.
const CONTROLLER_HEIGHT = { full: 84, compact: 52 };
const MARKET_ROW_HEIGHT = 46;

const CONTROLS_FULL_HEIGHT =
  CONTROLS_PADDING_TOP.full +
  MARKET_ROW_HEIGHT +
  CONTROLS_GAP.full +
  CONTROLLER_HEIGHT.full +
  CONTROLS_PADDING_BOTTOM.full;

const CONTROLS_COMPACT_HEIGHT =
  CONTROLS_PADDING_TOP.compact +
  MARKET_ROW_HEIGHT +
  CONTROLS_GAP.compact +
  CONTROLLER_HEIGHT.compact +
  CONTROLS_PADDING_BOTTOM.compact;

/**
 * compaction 진행 거리의 폴백(px). 실제로는 제목 영역 높이(T)를 측정해 그 거리에 맞춘다.
 * compaction 이 "status 탭이 상단에 붙는 순간"에 완료되도록 RANGE = T 로 둔다.
 */
const COMPACT_SCROLL_RANGE = 140;

/** full → compact 를 --order-compact(0~1)로 선형 보간하는 calc 문자열 생성기. */
const compactCalc = (full: number, compact: number) =>
  `calc(${full}px - ${full - compact}px * var(--order-compact, 0))`;

type OrderStickyLayoutContextValue = {
  /** 주문 상태 탭 높이 측정용 ref (sticky controls/table header top 기준값). */
  statusTabsRef: RefCallback<HTMLDivElement>;
  /** sticky controls 의 top (= 상태 탭 높이). 숫자 폴백 값. */
  stickyControlsTop: number;
  /** table split header 의 top 숫자 폴백. 실제 위치는 --order-table-header-top 변수로 계산된다. */
  tableHeaderTop: number;
  /**
   * 리스트 로드 완료 시 호출. 탭 전환/필터 변경은 로딩(전체 스피너)으로 콘텐츠가 붕괴되어
   * scrollTop 이 0 으로 강제되는데(= compact 풀림), min(변경 직전 위치, 제목 높이 T)로
   * 되돌린다. T 는 compact 가 100% 완성되는 scrollTop 이므로 T 보다 깊이 스크롤된
   * 상태였다면 compact 를 유지한 채 테이블 1행부터 보이고, T 미만이면(compact 진행 중)
   * 직전 위치 그대로 복원되어 compact 진행도가 보존된다.
   */
  restoreScrollOnLoad: () => void;
};

const noop = () => undefined;

const defaultContextValue: OrderStickyLayoutContextValue = {
  statusTabsRef: noop,
  stickyControlsTop: DEFAULT_STATUS_TABS_HEIGHT,
  tableHeaderTop: DEFAULT_STATUS_TABS_HEIGHT + CONTROLS_FULL_HEIGHT,
  restoreScrollOnLoad: noop,
};

const OrderStickyLayoutContext = createContext<OrderStickyLayoutContextValue>(defaultContextValue);

const getScrollableParent = (node: HTMLElement) => {
  let current: HTMLElement | null = node.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const isScrollable =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      current.scrollHeight > current.clientHeight;

    if (isScrollable) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
};

const useMeasuredHeight = (fallbackHeight: number) => {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(fallbackHeight);

  useEffect(() => {
    if (!node) return;

    const updateHeight = () => {
      setHeight(Math.round(node.getBoundingClientRect().height));
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [node]);

  return { height, ref: setNode, node };
};

export const OrderStickyLayoutProvider = ({ children }: { children: ReactNode }) => {
  const statusTabs = useMeasuredHeight(DEFAULT_STATUS_TABS_HEIGHT);
  const statusTabsNode = statusTabs.node;

  const [wrapperNode, setWrapperNode] = useState<HTMLDivElement | null>(null);

  const statusTabsNodeRef = useRef<HTMLDivElement | null>(statusTabsNode);
  statusTabsNodeRef.current = statusTabsNode;

  // 제목 영역 높이 T(= status 탭이 상단에 붙는 scrollTop). compaction RANGE 로 쓴다.
  const titleHeightRef = useRef(COMPACT_SCROLL_RANGE);
  // 정상(스크롤 가능) 상태에서의 마지막 scrollTop. 로딩 붕괴 시 복원 기준값.
  const lastStableScrollTopRef = useRef(0);

  /**
   * scroll root 의 스크롤 위치를 0~1 진행도로 환산해 `--order-compact` 변수에 기록한다.
   * React state 를 매 프레임 갱신하지 않고 style.setProperty 로만 써서 리렌더를 피한다.
   * (외부 시스템(스크롤)과의 동기화이므로 useEffect 사용이 적절하다.)
   */
  useEffect(() => {
    if (!wrapperNode) return;

    const scrollRoot = getScrollableParent(wrapperNode);

    const getMaxScroll = () =>
      scrollRoot
        ? scrollRoot.scrollHeight - scrollRoot.clientHeight
        : document.documentElement.scrollHeight - window.innerHeight;

    const scroller: HTMLElement | Window = scrollRoot ?? window;

    let frame = 0;

    // 스크롤이 최상단일 때 제목 영역 높이 T 를 측정한다(status 탭의 flow offset).
    const measureTitleHeight = (scrollTop: number) => {
      if (scrollTop > 0) return;
      const statusNode = statusTabsNodeRef.current;
      if (!statusNode) return;
      const rootTop = scrollRoot ? scrollRoot.getBoundingClientRect().top : 0;
      const titleHeight = Math.round(statusNode.getBoundingClientRect().top - rootTop);
      if (titleHeight > 0) {
        titleHeightRef.current = titleHeight;
      }
    };

    const writeProgress = () => {
      frame = 0;
      const scrollTop = scrollRoot ? scrollRoot.scrollTop : window.scrollY;
      measureTitleHeight(scrollTop);
      const range = Math.max(titleHeightRef.current, 1);
      const progress = Math.min(Math.max(scrollTop / range, 0), 1);
      wrapperNode.style.setProperty('--order-compact', progress.toFixed(3));

      // 콘텐츠가 충분히 길어 실제로 스크롤 가능한 정상 상태의 위치만 기억한다.
      // 로딩 스피너로 콘텐츠가 붕괴되면 scrollTop 이 0 으로 강제되는데, 그 값은 무시한다.
      if (getMaxScroll() > 1) {
        lastStableScrollTopRef.current = scrollTop;
      }
    };

    const handleScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(writeProgress);
    };

    writeProgress();
    scroller.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scroller.removeEventListener('scroll', handleScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [wrapperNode, statusTabsNode]);

  // 탭/필터 변경으로 리스트를 새로 가져오면 목표를 T 로 클램프해 복원한다.
  // compact 는 유지하되(>= T 였다면 그대로 1) 리스트는 항상 1행부터 보인다.
  const restoreScrollOnLoad = useCallback(() => {
    if (typeof window === 'undefined') return;
    const scrollRoot = wrapperNode ? getScrollableParent(wrapperNode) : null;
    const scroller: HTMLElement | Window = scrollRoot ?? window;
    const currentScroll = scrollRoot ? scrollRoot.scrollTop : window.scrollY;
    const target = Math.min(lastStableScrollTopRef.current, titleHeightRef.current);

    if (Math.abs(currentScroll - target) < 1) return;
    scroller.scrollTo({ top: target });
  }, [wrapperNode]);

  const value = useMemo<OrderStickyLayoutContextValue>(
    () => ({
      statusTabsRef: statusTabs.ref,
      stickyControlsTop: statusTabs.height,
      tableHeaderTop: statusTabs.height + CONTROLS_FULL_HEIGHT,
      restoreScrollOnLoad,
    }),
    [statusTabs.height, statusTabs.ref, restoreScrollOnLoad],
  );

  const wrapperStyle = useMemo<CSSProperties>(
    () =>
      ({
        '--order-status-h': `${statusTabs.height}px`,
        '--order-controls-pt': compactCalc(CONTROLS_PADDING_TOP.full, CONTROLS_PADDING_TOP.compact),
        '--order-controls-gap': compactCalc(CONTROLS_GAP.full, CONTROLS_GAP.compact),
        '--order-controls-pb': compactCalc(
          CONTROLS_PADDING_BOTTOM.full,
          CONTROLS_PADDING_BOTTOM.compact,
        ),
        '--order-controller-h': compactCalc(CONTROLLER_HEIGHT.full, CONTROLLER_HEIGHT.compact),
        '--order-controls-height': compactCalc(CONTROLS_FULL_HEIGHT, CONTROLS_COMPACT_HEIGHT),
        '--order-sticky-controls-top': 'var(--order-status-h)',
        '--order-table-header-top': 'calc(var(--order-status-h) + var(--order-controls-height))',
      }) as CSSProperties,
    [statusTabs.height],
  );

  return (
    <OrderStickyLayoutContext.Provider value={value}>
      <div
        ref={setWrapperNode}
        className="flex min-h-0 w-full min-w-0 flex-1 flex-col [overflow-anchor:none]"
        style={wrapperStyle}
      >
        {children}
      </div>
    </OrderStickyLayoutContext.Provider>
  );
};

export const useOrderStickyLayout = () => useContext(OrderStickyLayoutContext);
